/**
 * Utilities for detecting near-duplicate or multi-revision tender documents and forms
 * (e.g. original vs addendum with different date stamps, v1 vs v2, or PDF vs DOCX of the same schedule)
 * without collapsing legitimately distinct schedules (e.g. Schedule 1 vs Schedule 2).
 */

const KNOWN_DOC_EXTENSIONS = /\.(docx?|pdf|xlsx?|xlsm|csv|txt|pptx?|rtf|odt|ods)$/i;

const NUMERIC_DATE_PATTERNS = [
  // DD.MM.YYYY, DD-MM-YYYY, DD_MM_YYYY, DD.MM.YY, DD-MM-YY, DD_MM_YY
  /(?:^|[\s._\-()[\]])(?:0?[1-9]|[12]\d|3[01])[._-](?:0?[1-9]|1[0-2])[._-](?:20\d{2}|\d{2})(?=$|[\s._\-()[\]])/g,
  // YYYY.MM.DD, YYYY-MM-DD, YYYY_MM_DD
  /(?:^|[\s._\-()[\]])20\d{2}[._-](?:0?[1-9]|1[0-2])[._-](?:0?[1-9]|[12]\d|3[01])(?=$|[\s._\-()[\]])/g,
  // YYYYMMDD or DDMMYYYY
  /(?:^|[\s._\-()[\]])(?:20\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])|(?:0[1-9]|[12]\d|3[01])(?:0[1-9]|1[0-2])20\d{2})(?=$|[\s._\-()[\]])/g,
];

const TEXT_DATE_PATTERNS = [
  // DD Mon YYYY / DD Month YY
  /(?:^|[\s._\-()[\]])(?:0?[1-9]|[12]\d|3[01])[\s._-]*(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[\s._-]*(?:20\d{2}|\d{2})(?=$|[\s._\-()[\]])/gi,
  // Mon DD YYYY
  /(?:^|[\s._\-()[\]])(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)[\s._-]*(?:0?[1-9]|[12]\d|3[01])[\s._\-,]*(?:20\d{2}|\d{2})(?=$|[\s._\-()[\]])/gi,
];

const VERSION_REVISION_PATTERNS = [
  // v1, v1.2, ver 2, version 3, rev A, rev 1, revision 2, addendum 1, amendment 2
  /(?:^|[\s._\-()[\]])(?:v(?:er(?:sion)?)?|rev(?:ision)?|addendum|amend(?:ment)?)[\s._-]*\d+(?:[._]\d+)*[a-z]?(?=$|[\s._\-()[\]])/gi,
  /(?:^|[\s._\-()[\]])(?:rev(?:ision)?)[\s._-]+[a-z](?=$|[\s._\-()[\]])/gi,
  // Status modifiers like final, revised, updated, clean, tracked, copy
  /(?:^|[\s._\-()[\]])(?:final|revised|updated|superseded|amended|copy)(?=$|[\s._\-()[\]])/gi,
  // Trailing duplicate indices like (1), (2), [1], [2]
  /[\s._-]*(?:\(\d+\)|\[\d+\])$/g,
];

/**
 * Compute a normalized comparison stem for a filename by stripping path prefixes,
 * file extensions, date stamps, and explicit version/revision tags.
 */
export function normalizeDocumentComparisonKey(filename: string): string {
  if (!filename) {
    return '';
  }
  const bareName = filename.replace(/\\/g, '/').split('/').pop() || filename;
  let stem = bareName.replace(KNOWN_DOC_EXTENSIONS, '');

  for (const pattern of NUMERIC_DATE_PATTERNS) {
    stem = stem.replace(pattern, ' ');
  }
  for (const pattern of TEXT_DATE_PATTERNS) {
    stem = stem.replace(pattern, ' ');
  }
  for (const pattern of VERSION_REVISION_PATTERNS) {
    stem = stem.replace(pattern, ' ');
  }

  return stem
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Group distinct filenames that collapse to the same normalized stem (minimum 4 chars).
 * Returns an array of groups, where each group has 2 or more filenames.
 */
export function findSimilarDocumentGroups(filenames: string[]): string[][] {
  if (!Array.isArray(filenames) || filenames.length < 2) {
    return [];
  }

  const uniqueFilenames: string[] = [];
  const seenExact = new Set<string>();
  for (const raw of filenames) {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      continue;
    }
    const lower = trimmed.toLowerCase();
    if (!seenExact.has(lower)) {
      seenExact.add(lower);
      uniqueFilenames.push(trimmed);
    }
  }

  const byKey = new Map<string, string[]>();
  for (const fname of uniqueFilenames) {
    const key = normalizeDocumentComparisonKey(fname);
    if (key.length < 4) {
      continue;
    }
    const bucket = byKey.get(key);
    if (bucket) {
      bucket.push(fname);
    } else {
      byKey.set(key, [fname]);
    }
  }

  const groups: string[][] = [];
  for (const group of byKey.values()) {
    if (group.length >= 2) {
      groups.push(group);
    }
  }
  return groups;
}

/**
 * Map each filename that belongs to a similar-document group to its counterpart filename(s).
 */
export function getSimilarDocumentMap(filenames: string[]): Record<string, string[]> {
  const groups = findSimilarDocumentGroups(filenames);
  const map: Record<string, string[]> = {};
  for (const group of groups) {
    for (const fname of group) {
      map[fname] = group.filter((other) => other !== fname);
    }
  }
  return map;
}

/**
 * Truncate a long filename in the middle so both the leading identifier and the
 * trailing date/version/extension (e.g. `_12.10.26.docx` vs `_16.10.26.docx`) remain visible.
 */
export function formatMiddleTruncatedFilename(filename: string, maxLength = 42): string {
  if (!filename || filename.length <= maxLength) {
    return filename;
  }
  const ellipsis = '...';
  const available = Math.max(maxLength - ellipsis.length, 12);
  const tailLength = Math.min(Math.max(Math.ceil(available * 0.45), 14), filename.length - 4);
  const headLength = Math.max(available - tailLength, 6);
  return `${filename.slice(0, headLength)}${ellipsis}${filename.slice(filename.length - tailLength)}`;
}
